"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { Document } from "@/services/api/documents/types";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import FormInput from "@/components/ui/form-input";
import TextArea from "@/components/ui/form-input/TextArea";
import { Alert } from "@/components/ui/alert/Alert";
import ButtonGroup from "@/components/ui/button-group";
import { FileUploader } from "@/components/documents/FileUploader";
import { useComposerVoiceInput } from "@/components/chat/useComposerVoiceInput";
import { TranscriptionAPI } from "@/services/api";
import * as AgenticV2API from "@/services/api/agentic-v2";

export type IntakeChannel = "voice" | "email" | "transcript";
type VoiceMode = "paste" | "upload" | "record" | "zoom";
type EmailMode = "paste" | "upload";
type TranscriptMode = "paste" | "upload";

const CHANNEL_OPTIONS = [
  { value: "voice" as const, label: "Voice" },
  { value: "email" as const, label: "Email" },
  { value: "transcript" as const, label: "Transcript" },
];

const VOICE_MODE_OPTIONS = [
  { value: "paste" as const, label: "Paste" },
  { value: "upload" as const, label: "Upload" },
  { value: "record" as const, label: "Record" },
  { value: "zoom" as const, label: "Zoom" },
];

const EMAIL_MODE_OPTIONS = [
  { value: "paste" as const, label: "Paste" },
  { value: "upload" as const, label: ".eml upload" },
];

const TRANSCRIPT_MODE_OPTIONS = [
  { value: "paste" as const, label: "Paste" },
  { value: "upload" as const, label: "Upload file" },
];

const AUDIO_ACCEPT = [
  "audio/mpeg",
  "audio/wav",
  "audio/mp4",
  "audio/x-m4a",
  "audio/webm",
  "audio/flac",
] as const;

const TRANSCRIPT_ACCEPT = [
  "text/plain",
  "text/vtt",
  "application/x-subrip",
  "application/octet-stream",
] as const;

const CHANNEL_COPY: Record<IntakeChannel, { title: string; description: string }> = {
  voice: {
    title: "Voice lead intake",
    description: "Add adviser call audio or notes to start onboarding capture from a voice channel.",
  },
  email: {
    title: "Email lead intake",
    description: "Paste or upload a lead enquiry email to start onboarding capture.",
  },
  transcript: {
    title: "Transcript lead intake",
    description: "Paste or upload a call or video transcript to start onboarding capture.",
  },
};

export type OnboardingIntakeDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  initialChannel?: IntakeChannel;
  missionId?: string;
  onBridged?: (result: AgenticV2API.BridgeIntakeResponse) => void;
};

export function OnboardingIntakeDialog({
  isOpen,
  onClose,
  initialChannel = "voice",
  missionId,
  onBridged,
}: OnboardingIntakeDialogProps) {
  const [channel, setChannel] = useState<IntakeChannel>(initialChannel);
  const [voiceMode, setVoiceMode] = useState<VoiceMode>("paste");
  const [emailMode, setEmailMode] = useState<EmailMode>("paste");
  const [transcriptMode, setTranscriptMode] = useState<TranscriptMode>("paste");
  const [text, setText] = useState("");
  const [meetingId, setMeetingId] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailFrom, setEmailFrom] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clearEntryState = useCallback(() => {
    setText("");
    setMeetingId("");
    setEmailSubject("");
    setEmailFrom("");
    setSelectedFile(null);
    setSubmitError(null);
  }, []);

  const reset = useCallback(() => {
    setChannel(initialChannel);
    setVoiceMode("paste");
    setEmailMode("paste");
    setTranscriptMode("paste");
    clearEntryState();
    setIsSubmitting(false);
  }, [clearEntryState, initialChannel]);

  const handleChannelChange = useCallback(
    (next: IntakeChannel) => {
      if (next === channel) return;
      clearEntryState();
      setChannel(next);
    },
    [channel, clearEntryState],
  );

  const handleVoiceModeChange = useCallback(
    (next: VoiceMode) => {
      if (next === voiceMode) return;
      clearEntryState();
      setVoiceMode(next);
    },
    [clearEntryState, voiceMode],
  );

  const handleEmailModeChange = useCallback(
    (next: EmailMode) => {
      if (next === emailMode) return;
      clearEntryState();
      setEmailMode(next);
    },
    [clearEntryState, emailMode],
  );

  const handleTranscriptModeChange = useCallback(
    (next: TranscriptMode) => {
      if (next === transcriptMode) return;
      clearEntryState();
      setTranscriptMode(next);
    },
    [clearEntryState, transcriptMode],
  );

  const validateSelectedFile = useCallback(
    (file: File | null) => {
      if (!file) return null;
      if (channel === "voice" && voiceMode === "upload") {
        const isAudio =
          AUDIO_ACCEPT.includes(file.type as (typeof AUDIO_ACCEPT)[number]) ||
          /\.(mp3|wav|m4a|webm|flac|mp4)$/i.test(file.name);
        if (!isAudio) {
          return "Choose an audio file for voice upload.";
        }
      }
      if (channel === "email" && emailMode === "upload") {
        if (!file.name.toLowerCase().endsWith(".eml")) {
          return "Choose a .eml file for email upload.";
        }
      }
      if (channel === "transcript" && transcriptMode === "upload") {
        const isTranscript =
          TRANSCRIPT_ACCEPT.includes(file.type as (typeof TRANSCRIPT_ACCEPT)[number]) ||
          /\.(txt|vtt|srt)$/i.test(file.name);
        if (!isTranscript) {
          return "Choose a .txt, .vtt, or .srt transcript file.";
        }
      }
      return null;
    },
    [channel, emailMode, transcriptMode, voiceMode],
  );

  const handleFileSelected = useCallback(
    (file: File | null) => {
      const validationError = validateSelectedFile(file);
      if (validationError) {
        setSelectedFile(null);
        setSubmitError(validationError);
        return;
      }
      setSubmitError(null);
      setSelectedFile(file);
    },
    [validateSelectedFile],
  );

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  useEffect(() => {
    if (isOpen) setChannel(initialChannel);
  }, [initialChannel, isOpen]);

  const { isListening, toggleVoiceInput } = useComposerVoiceInput({
    getBaseText: () => text,
    onTranscriptChange: setText,
  });

  const handleClose = () => {
    if (!isSubmitting) {
      if (isListening) void toggleVoiceInput();
      reset();
      onClose();
    }
  };

  const transcribeUpload = async (file: File): Promise<string> => {
    const contentType = file.type && file.type.length > 0 ? file.type : "audio/mpeg";
    const upload = await TranscriptionAPI.getUploadUrl({
      fileName: file.name,
      contentType,
    });
    await TranscriptionAPI.putAudioToSasUrl(upload.sas_url, file, contentType);
    const job = await TranscriptionAPI.createJob({
      audioUri: upload.audio_uri,
      language: "en-GB",
      diarization: true,
    });
    const deadline = Date.now() + 5 * 60_000;
    while (Date.now() < deadline) {
      const status = await TranscriptionAPI.getJob(job.job_id);
      if (status.state === "succeeded" && status.transcript?.length) {
        return status.transcript.map((row) => row.text).join("\n");
      }
      if (status.state === "failed") {
        throw new Error(status.error || "Transcription failed");
      }
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }
    throw new Error("Transcription timed out — try again or paste the transcript.");
  };

  const readTextFile = async (file: File): Promise<string> => {
    const content = await file.text();
    if (!content.trim()) {
      throw new Error("Uploaded file is empty.");
    }
    return content.trim();
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    const fileError = validateSelectedFile(selectedFile);
    if (fileError) {
      setSubmitError(fileError);
      return;
    }
    setIsSubmitting(true);
    try {
      if (channel === "voice") {
        let resolvedText = text.trim();
        let source: AgenticV2API.BridgeIntakeInput["source"] = "paste";
        let resolvedMeetingId: string | undefined;

        if (voiceMode === "zoom") {
          resolvedMeetingId = meetingId.trim() || undefined;
          source = "zoom";
        } else if (voiceMode === "record") {
          if (isListening) await toggleVoiceInput();
          source = "record";
        } else if (voiceMode === "upload") {
          if (!selectedFile) throw new Error("Choose an audio file to upload.");
          resolvedText = await transcribeUpload(selectedFile);
          source = "upload";
        }

        const result = await AgenticV2API.bridgeOnboardingIntake({
          channel: "voice",
          mission_id: missionId,
          text: resolvedText || undefined,
          meeting_id: resolvedMeetingId,
          source,
        });
        onBridged?.(result);
      } else if (channel === "email") {
        if (emailMode === "upload") {
          if (!selectedFile) throw new Error("Choose a .eml file to upload.");
          const result = await AgenticV2API.bridgeOnboardingIntakeEml(selectedFile, missionId);
          onBridged?.(result);
        } else {
          const result = await AgenticV2API.bridgeOnboardingIntake({
            channel: "email",
            mission_id: missionId,
            text: text.trim(),
            subject: emailSubject.trim() || undefined,
            from_address: emailFrom.trim() || undefined,
            source: "paste",
          });
          onBridged?.(result);
        }
      } else {
        let resolvedText = text.trim();
        if (transcriptMode === "upload") {
          if (!selectedFile) throw new Error("Choose a transcript file to upload.");
          resolvedText = await readTextFile(selectedFile);
        }
        const result = await AgenticV2API.bridgeOnboardingIntake({
          channel: "transcript",
          mission_id: missionId,
          text: resolvedText,
          source: transcriptMode === "upload" ? "upload" : "paste",
        });
        onBridged?.(result);
      }
      reset();
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Could not start intake.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = useMemo(() => {
    if (channel === "voice") {
      if (voiceMode === "zoom") return Boolean(meetingId.trim() || text.trim());
      if (voiceMode === "upload") return Boolean(selectedFile);
      return Boolean(text.trim());
    }
    if (channel === "email") {
      if (emailMode === "upload") return Boolean(selectedFile);
      return Boolean(text.trim());
    }
    if (transcriptMode === "upload") return Boolean(selectedFile);
    return Boolean(text.trim());
  }, [channel, emailMode, meetingId, selectedFile, text, transcriptMode, voiceMode]);

  const noopUpload = useCallback(async (_file: File): Promise<Document> => {
    throw new Error("Deferred upload: this should not run.");
  }, []);

  const copy = CHANNEL_COPY[channel];

  return (
    <Dialog open={isOpen} onClose={handleClose} size="2xl" pinToViewport>
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogDescription>{copy.description}</DialogDescription>
      <DialogBody>
        <div className="space-y-5">
          <div data-testid="intake-channel-group">
            <ButtonGroup
              options={CHANNEL_OPTIONS}
              value={channel}
              onChange={handleChannelChange}
              size="sm"
              fullWidth
            />
          </div>

          {channel === "voice" ? (
            <>
              <div data-testid="voice-intake-mode-group">
                <ButtonGroup
                  options={VOICE_MODE_OPTIONS}
                  value={voiceMode}
                  onChange={handleVoiceModeChange}
                  size="sm"
                  fullWidth
                />
              </div>
              {voiceMode === "paste" ? (
                <TextArea
                  label="Transcript"
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  rows={10}
                  placeholder="Paste adviser call notes or transcript text…"
                  data-testid="voice-intake-transcript"
                />
              ) : null}
              {voiceMode === "upload" ? (
                <FileUploader
                  uploadFunction={noopUpload}
                  acceptedMimeTypes={[...AUDIO_ACCEPT]}
                  maxSize={100 * 1024 * 1024}
                  multiple={false}
                  onFileSelected={handleFileSelected}
                />
              ) : null}
              {voiceMode === "record" ? (
                <div className="space-y-3">
                  <TextArea
                    label="Live transcript"
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    rows={8}
                    placeholder="Start recording to capture speech…"
                    data-testid="voice-intake-record-transcript"
                  />
                  <Button
                    type="button"
                    color={isListening ? "rose" : "slate"}
                    onClick={() => void toggleVoiceInput()}
                  >
                    {isListening ? "Stop recording" : "Start recording"}
                  </Button>
                </div>
              ) : null}
              {voiceMode === "zoom" ? (
                <div className="space-y-3">
                  <FormInput
                    label="Meeting ID"
                    value={meetingId}
                    onChange={(event) => setMeetingId(event.target.value)}
                    placeholder="Pull pre-transcribed Zoom meeting"
                    data-testid="voice-intake-meeting-id"
                  />
                  <TextArea
                    label="Or paste transcript"
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    rows={6}
                    placeholder="Optional fallback if meeting transcript is not ready"
                  />
                </div>
              ) : null}
            </>
          ) : null}

          {channel === "email" ? (
            <>
              <div data-testid="email-intake-mode-group">
                <ButtonGroup
                  options={EMAIL_MODE_OPTIONS}
                  value={emailMode}
                  onChange={handleEmailModeChange}
                  size="sm"
                  fullWidth
                />
              </div>
              {emailMode === "paste" ? (
                <div className="space-y-3">
                  <FormInput
                    label="From"
                    value={emailFrom}
                    onChange={(event) => setEmailFrom(event.target.value)}
                    placeholder="jane.smith@example.com"
                    data-testid="email-intake-from"
                  />
                  <FormInput
                    label="Subject"
                    value={emailSubject}
                    onChange={(event) => setEmailSubject(event.target.value)}
                    placeholder="Re: pension review enquiry"
                    data-testid="email-intake-subject"
                  />
                  <TextArea
                    label="Email body"
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    rows={10}
                    placeholder="Paste the email body…"
                    data-testid="email-intake-body"
                  />
                </div>
              ) : (
                <FileUploader
                  uploadFunction={noopUpload}
                  acceptedMimeTypes={["message/rfc822", "application/octet-stream"]}
                  maxSize={10 * 1024 * 1024}
                  multiple={false}
                  onFileSelected={handleFileSelected}
                />
              )}
            </>
          ) : null}

          {channel === "transcript" ? (
            <>
              <div data-testid="transcript-intake-mode-group">
                <ButtonGroup
                  options={TRANSCRIPT_MODE_OPTIONS}
                  value={transcriptMode}
                  onChange={handleTranscriptModeChange}
                  size="sm"
                  fullWidth
                />
              </div>
              {transcriptMode === "paste" ? (
                <TextArea
                  label="Transcript"
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  rows={10}
                  placeholder="Paste call or video transcript…"
                  data-testid="transcript-intake-body"
                />
              ) : (
                <FileUploader
                  uploadFunction={noopUpload}
                  acceptedMimeTypes={[...TRANSCRIPT_ACCEPT]}
                  maxSize={10 * 1024 * 1024}
                  multiple={false}
                  onFileSelected={handleFileSelected}
                />
              )}
            </>
          ) : null}

          {submitError ? <Alert variant="error">{submitError}</Alert> : null}
        </div>
      </DialogBody>
      <DialogActions>
        <Button type="button" plain onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="button"
          color="indigo"
          onClick={() => void handleSubmit()}
          disabled={!canSubmit || isSubmitting}
          data-testid="intake-submit"
        >
          {isSubmitting ? "Starting capture…" : missionId ? "Update intake" : "Start capture"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
